import React from 'react';
import { senCategoryLabel, senStatusLabel, normalizeSpecialNeeds } from '../utils/childFields';

/**
 * ChildCareFlags — compact, accessible row of badges that surfaces a child's
 * dietary requirements and Special Educational Needs (SEND) status.
 *
 * Designed for high-density staff surfaces:
 *   - Live Ratios room cards (point-of-care)
 *   - Daily attendance roster rows
 *   - Driver / transit manifests
 *   - Child profile overview header
 *
 * Allergies are shown by default (existing behaviour) but can be hidden when
 * the parent screen renders them separately.
 *
 * Color guidance:
 *   • Red    — allergies (life-threatening if missed)
 *   • Amber  — SEND categories (planning info, never sensitive disclosure)
 *   • Blue   — dietary requirements (kitchen routing)
 */
export function ChildCareFlags({
  child,
  showAllergies = true,
  size = 'sm',           // 'sm' | 'xs'
  testIdPrefix = '',
}) {
  if (!child) return null;
  const allergies = Array.isArray(child.allergies) ? child.allergies : [];
  const dietary = Array.isArray(child.dietary_requirements) ? child.dietary_requirements : [];
  const sn = normalizeSpecialNeeds(child.special_needs);
  const hasAnything = (
    (showAllergies && allergies.length > 0)
    || dietary.length > 0
    || sn.has_sen
  );
  if (!hasAnything) return null;

  const padding = size === 'xs' ? '0.1rem 0.45rem' : '0.2rem 0.55rem';
  const fontSize = size === 'xs' ? '0.7rem' : '0.75rem';
  const baseChip = {
    padding,
    fontSize,
    borderRadius: '999px',
    fontWeight: 600,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
  };

  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center' }}
      data-testid={`${testIdPrefix}care-flags`}
    >
      {showAllergies && allergies.map((a, i) => (
        <span
          key={`al-${i}`}
          title={`Allergy: ${a}`}
          data-testid={`${testIdPrefix}flag-allergy-${i}`}
          style={{
            ...baseChip,
            background: 'hsl(0 80% 95%)',
            color: 'hsl(0 75% 35%)',
            border: '1px solid hsl(0 70% 85%)',
          }}
        >
          ⚠ {a}
        </span>
      ))}

      {sn.has_sen && (
        <span
          title={`SEND — ${senStatusLabel(sn.sen_status)}${sn.categories.length ? ': ' + sn.categories.map(senCategoryLabel).join(', ') : ''}`}
          data-testid={`${testIdPrefix}flag-sen`}
          style={{
            ...baseChip,
            background: 'hsl(35 90% 94%)',
            color: 'hsl(30 80% 30%)',
            border: '1px solid hsl(35 80% 80%)',
          }}
        >
          SEND{sn.sen_status === 'ehcp' ? ' · EHCP' : ''}
        </span>
      )}

      {dietary.map((d, i) => (
        <span
          key={`d-${i}`}
          title={`Dietary: ${d}`}
          data-testid={`${testIdPrefix}flag-diet-${i}`}
          style={{
            ...baseChip,
            background: 'hsl(210 90% 95%)',
            color: 'hsl(215 70% 32%)',
            border: '1px solid hsl(210 75% 85%)',
          }}
        >
          🍽 {d}
        </span>
      ))}
    </div>
  );
}

export default ChildCareFlags;
